import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { HugeiconsIcon } from '@hugeicons/react'
import { FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { selectOrganisationId, selectWebStoreFoldingEnabled } from '@/app/slices/organisationSlice'
import SecondaryProcessRow from '@/features/secondary-processes/SecondaryProcessRow'

const SecondaryProcessesBulkEditModal = ({
    onClose,
    onSaveClose,
    open,
    partsAreRotary,
    secondaryProcesses,
    showFolding,
}) => {
    const { t } = useTranslation()

    const organisationId = useSelector(selectOrganisationId)
    const foldingEnabled = useSelector(selectWebStoreFoldingEnabled)
    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const [enabledProcesses, setEnabledProcesses] = useState(secondaryProcesses || [])
    const [isFoldingVisible, setIsFoldingVisible] = useState(showFolding)

    const onChangeFolding = (event) => {
        const { checked } = event.target

        setIsFoldingVisible(checked ? 'all' : false)
        //folding is not a secondary process, but we need to save it in the same way
        if (checked) {
            const foldingProcess = {
                secondaryProcessId: 'Folding',
                name: 'Folding',
                isEnabled: true,
            }
            setEnabledProcesses([...enabledProcesses, foldingProcess])
        }
        if (!checked) {
            setEnabledProcesses(enabledProcesses.filter((process) => process.secondaryProcessId !== 'Folding'))
        }
    }

    const onCheckboxChange = (event) => {
        const { checked, value } = event.target

        const enabledProcess = enabledProcesses.find((p) => p.secondaryProcessId === value)

        if (enabledProcess) {
            enabledProcess.isEnabled = checked
            const newArray = [...enabledProcesses.filter((p) => p.secondaryProcessId !== value), enabledProcess]
            setEnabledProcesses(newArray)
        } else {
            const processToEnable = {
                secondaryProcessId: value,
                isEnabled: true,
                input:
                    organisationSecondaryProcesses.find((p) => p.secondaryProcessId === value).measureType ===
                    'Quantity'
                        ? 1
                        : 0,
            }
            setEnabledProcesses([...enabledProcesses, processToEnable])
        }
    }

    const getProcessDetails = (processId) =>
        organisationSecondaryProcesses?.find((osp) => osp.secondaryProcessId === processId)

    const formattedProcesses = useMemo(() => {
        const newProcessList = enabledProcesses.map((process) => {
            //hanlde folding process
            if (process.secondaryProcessId === 'Folding') {
                return {
                    ...process,
                    isEnabled: true,
                }
            }
            const { $type, ...cleanProcess } = process
            const processDetails = getProcessDetails(process.secondaryProcessId)

            return {
                ...cleanProcess,
                index: processDetails?.index,
                input: processDetails?.measureType === 'Quantity' ? process.input || 1 : 0,
            }
        })

        // if isFoldingVisible but enabledProcesses don have it, add it anyway to newprocessList
        if (isFoldingVisible && !enabledProcesses.some((process) => process.secondaryProcessId === 'Folding')) {
            const foldingProcess = {
                secondaryProcessId: 'Folding',
                name: 'Folding',
                isEnabled: true,
            }
            newProcessList.push(foldingProcess)
        }

        return [...newProcessList]
    }, [enabledProcesses])

    const filteredProcesses = useMemo(() => {
        return organisationSecondaryProcesses.filter(
            (process) => !process.isDeleted && process.webStoreEnabled && process.customerVisible
        )
    }, [organisationSecondaryProcesses])

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    sx: { width: 380 },
                },
            }}
            onClose={() => {
                setEnabledProcesses([])
                onClose()
            }}
        >
            <DialogTitle
                alignItems="center"
                data-testid="bulk-edit-secondary-process-modal"
                display="flex"
                justifyContent="space-between"
            >
                <Typography variant="strong1">{t('SECONDARY PROCESSES')}</Typography>
                <IconButton
                    color="secondary"
                    data-testid="order-item-secondary-process-save-button"
                    onClick={() => onSaveClose(formattedProcesses)}
                >
                    <HugeiconsIcon icon={FloppyDiskIcon} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    {t('Select the secondary processes you want to use in the selected parts.')}
                </Typography>
                <Box sx={{ mt: 3 }}>
                    {foldingEnabled && !partsAreRotary ? (
                        <SecondaryProcessRow key="folding-row">
                            <Checkbox
                                checked={isFoldingVisible === 'all' ? true : false}
                                color="secondary"
                                data-testid="secondary-process-option-checkbox"
                                indeterminate={isFoldingVisible === 'some' ? true : false}
                                size="small"
                                onChange={onChangeFolding}
                            />
                            <Typography data-testid="secondary-process-option-label">{t('Folding')}</Typography>
                        </SecondaryProcessRow>
                    ) : null}
                    {filteredProcesses.map((process) => (
                        <SecondaryProcessRow key={process.secondaryProcessId}>
                            <Checkbox
                                checked={
                                    enabledProcesses.find((ep) => ep.secondaryProcessId === process.secondaryProcessId)
                                        ?.isEnabled
                                }
                                color="secondary"
                                data-testid="bulk-edit-secondary-process-modal-option-checkbox"
                                indeterminate={
                                    enabledProcesses.find((ep) => ep.secondaryProcessId === process.secondaryProcessId)
                                        ?.isEnabled === null
                                }
                                size="small"
                                value={process.secondaryProcessId}
                                onChange={onCheckboxChange}
                            />
                            <Typography data-testid="bulk-edit-secondary-process-modal-option-label">
                                {process.name}
                            </Typography>
                        </SecondaryProcessRow>
                    ))}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

SecondaryProcessesBulkEditModal.propTypes = {
    open: PropTypes.bool,
    partsAreRotary: PropTypes.bool,
    secondaryProcesses: PropTypes.array,
    showFolding: PropTypes.bool,
    onClose: PropTypes.func,
    onSaveClose: PropTypes.func,
}

export default SecondaryProcessesBulkEditModal
